import { api } from './api';
import type { LotBasis } from './catalog';

/** Этап 1.3: спецификации клиента на сервере — строки со снимками себестоимости/цены, прямые расходы план/факт,
 * показатели (товарная маржа, вклад после прямых расходов), согласования ниже порога и переходы статусов. */
export type SpecStatus = 'Draft' | 'Quoted' | 'Won' | 'Lost' | 'Cancelled';
export type CostKind = 'pickup' | 'delivery' | 'installation' | 'other';
export type ApprovalLevel = 'below_min' | 'loss';

export type SpecMetrics = {
  saleTotal: number;
  vatTotal: number;
  totalInclVat: number;
  costTotal: number;
  productMargin: number;
  productMarginPct: number | null;
  directCostsPlan: number;
  contributionPlan: number;
  contributionPlanPct: number | null;
  directCostsActual: number | null;
  contributionActual: number | null;
  contributionActualPct: number | null;
  thresholds: { minProductMarginPct: number; minContributionPct: number };
  noCbm: string[];
  approvalLevel: ApprovalLevel | null;
};

export type SpecSummary = {
  id: string;
  code: string;
  project: string | null;
  client: string | null;
  siteAddress: string | null;
  ownerId: string | null;
  ownerName: string | null;
  deliveryDate: string | null;
  status: SpecStatus;
  version: number;
  quoteValidUntil: string | null;
  metrics: SpecMetrics;
  lineCount: number | null;
  units: number | null;
  createdAt: string;
  updatedAt: string;
};

export type SpecLine = {
  id: string;
  sku: string;
  supplierArticle: string | null;
  name: string;
  category: string;
  source: 'Own' | 'Partner';
  partnerId: string | null;
  partnerName: string | null;
  image: string | null;
  qty: number;
  available: number;
  incoming: number;
  incomingEta: string | null;
  option: string | null;
  optionSurcharge: number;
  options: { label: string; surcharge: number }[];
  unitCost: number | null;
  sale: number | null;
  manualPrice: boolean;
  calcSale: number | null;
  calcCost: number | null;
  priceBasis: string | null;
  minResale: number | null;
  markup: number;
  markupSource: string;
  discountPct: number;
  basis: LotBasis[];
  marginAed: number | null;
  marginPct: number | null;
  lineSale: number;
  lineCost: number;
  lineMargin: number;
  allocatedDirectCosts: number;
  contribution: number;
  contributionPct: number | null;
  cbm: number | null;
  lineCbm: number | null;
  availabilityLevel: 'info' | 'confirmed' | 'reserved';
  short: number;
  sort: number;
  version: number;
};

export type SpecCost = {
  id: string;
  kind: CostKind;
  stage: 'plan' | 'actual';
  amount: number;
  basis: Record<string, unknown> | null;
  comment: string | null;
  overridden: boolean;
  enteredBy: string | null;
  updatedAt: string | null;
};

export type SpecApproval = {
  id: string;
  level: ApprovalLevel;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Invalidated';
  specVersion: number;
  reason: string | null;
  snapshot: SpecMetrics | null;
  requestedBy: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  createdAt: string;
};

export type SpecFull = SpecSummary & {
  lines: SpecLine[];
  costs: { plan: SpecCost[]; actual: SpecCost[] };
  approvals: SpecApproval[];
  approval: { level: ApprovalLevel | null; ok: boolean; pending: SpecApproval | null };
  transitions: SpecStatus[];
  blockers: Record<string, string[]>;
  split: { partnerId: string | null; partner: string; own: boolean; lines: number; units: number; total: number }[];
  frozen: boolean;
};

export const specsApi = {
  list: (status?: SpecStatus) => api.get<SpecSummary[]>(`/specifications${status ? `?status=${status}` : ''}`),
  create: (body: { client?: string; project?: string; deliveryDate?: string } = {}) => api.post<SpecFull>('/specifications', body),
  get: (id: string) => api.get<SpecFull>(`/specifications/${id}`),
  patch: (id: string, body: { client?: string; project?: string; siteAddress?: string; deliveryDate?: string | null; ownerId?: string }) => api.patch<SpecFull>(`/specifications/${id}`, body),
  addLine: (id: string, body: { sku: string; qty: number; option?: string | null }) => api.post<SpecFull & { lineId: string }>(`/specifications/${id}/lines`, body),
  patchLine: (id: string, lineId: string, body: { qty?: number; option?: string | null; price?: number | null }) => api.patch<SpecFull>(`/specifications/${id}/lines/${lineId}`, body),
  removeLine: (id: string, lineId: string) => api.del<SpecFull>(`/specifications/${id}/lines/${lineId}`),
  alternatives: (id: string, sku: string) => api.get<import('./catalog').CatalogRow[]>(`/specifications/${id}/alternatives?sku=${encodeURIComponent(sku)}`),
  setCost: (id: string, body: { kind: CostKind; stage: 'plan' | 'actual'; amount: number | null; comment?: string | null }) => api.put<SpecFull>(`/specifications/${id}/costs`, body),
  reprice: (id: string) => api.post<SpecFull>(`/specifications/${id}/reprice`, {}),
  requestApproval: (id: string, reason: string) => api.post<SpecFull>(`/specifications/${id}/approvals`, { reason }),
  decide: (id: string, approvalId: string, approve: boolean, comment?: string) => api.post<SpecFull>(`/specifications/${id}/approvals/${approvalId}/decide`, { approve, comment }),
  setStatus: (id: string, status: SpecStatus, reason?: string) => api.post<SpecFull>(`/specifications/${id}/status`, { status, reason }),
};
